import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { PlatformService, StaticImageUrlPipe } from '@rma/generic/util/environment';
import { GoogleMapsStaticImageService } from '@rma/maps/api';
import {
  DEFAULT_GOOGLE_MAP_OPTIONS,
  DEFAULT_GOOGLE_STATIC_MAP_CONFIG,
  GOOGLE_MAPS_STATIC_MARKER,
  GoogleMapsStaticMapRequest,
  GoogleStaticMapConfig,
  RmaMapMarker,
} from '@rma/maps/domain';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { catchError, Observable, of } from 'rxjs';

@Component({
  selector: 'rma-static-google-map',
  imports: [AsyncPipe, LazyLoadImageModule, MatButtonModule, RouterModule, StaticImageUrlPipe, TranslocoModule],
  templateUrl: './static-google-map.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StaticGoogleMapComponent implements OnChanges {
  @Input()
  config: GoogleStaticMapConfig | null = DEFAULT_GOOGLE_STATIC_MAP_CONFIG;

  @Input()
  options: google.maps.MapOptions = { ...DEFAULT_GOOGLE_MAP_OPTIONS, styles: undefined };

  @Input()
  markers: RmaMapMarker[] | null | undefined = [];

  @Input()
  staticMapUrl: string | undefined;

  @Output()
  mapClicked = new EventEmitter<void>();

  private readonly platformService = inject(PlatformService);
  private readonly googleMapsStaticImageService = inject(GoogleMapsStaticImageService);

  mapUrl$: Observable<string> | undefined;

  ngOnChanges(changes: SimpleChanges): void {
    const { markers, config } = changes;

    if ((config || markers) && this.config && this.markers && this.platformService.isPlatformBrowser) {
      this.mapUrl$ = this.getStaticMapUrlWithSignature(this.config, this.markers).pipe(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        catchError(() => this.getStaticMapUrl(this.config!, this.markers)),
      );
    }
  }

  private getStaticMapUrlWithSignature(
    staticMapConfig: GoogleStaticMapConfig,
    markers: RmaMapMarker[] | null | undefined,
  ): Observable<string> {
    const request: GoogleMapsStaticMapRequest = {
      markerParameterStyle: staticMapConfig.markerParameterStyle ?? GOOGLE_MAPS_STATIC_MARKER,
      showMarker: true,
      showPointOfInterest: this.config?.showPoi === 'on',
      enableZoom: staticMapConfig.enableZoom,
      dimensions: {
        width: staticMapConfig.width,
        height: staticMapConfig.height,
        zoom: staticMapConfig.zoom,
        scale: staticMapConfig.scale,
      },
      cache: this.config?.cache,
    };

    if (!markers?.length) {
      return this.googleMapsStaticImageService.getStaticImageWithSignature(request);
    }

    return this.googleMapsStaticImageService.getStaticImageWithSignature({
      ...request,
      locations: markers.map((m) => ({
        coords: m.detail.coords,
        markerImageUrl: m.detail.icon ? `icon:${m.detail.icon}` : undefined,
      })),
    });
  }

  private getStaticMapUrl(staticMapConfig: GoogleStaticMapConfig, markers: RmaMapMarker[] | null | undefined): Observable<string> {
    return of(
      this.googleMapsStaticImageService.getStaticImage(
        staticMapConfig,
        markers?.map((m) => ({
          icon: m.detail.icon ?? GOOGLE_MAPS_STATIC_MARKER,
          coords: { lat: m.detail.coords.lat, lng: m.detail.coords.lon },
        })) ?? [],
      ),
    );
  }
}
